/**
 * Recently viewed products
 *
 * Tracks the products a customer views (client-side only, via localStorage)
 * and renders them elsewhere in the theme using Shopify's Section Rendering
 * API, so every "recently viewed" card is a real, server-rendered product
 * card rather than a hand-built duplicate.
 *
 * This file has no imports on purpose: the rest of the theme's JS is bundled
 * by a build step that isn't part of this repo, so a self-contained file is
 * the only way to guarantee `assets/recently-viewed-products.js` behaves the
 * same as its source.
 */

const STORAGE_KEY = 'recently-viewed-products';
const MAX_STORED_HANDLES = 24;

/**
 * @returns {string[]} Stored product handles, most recently viewed first.
 */
function getStoredHandles() {
  try {
    const handles = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return Array.isArray(handles) ? handles.filter((handle) => typeof handle === 'string') : [];
  } catch (error) {
    return [];
  }
}

/**
 * @param {string[]} handles - Product handles to persist, most recent first.
 */
function setStoredHandles(handles) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
  } catch (error) {
    // Storage unavailable (private browsing, quota exceeded, etc.) - ignore.
  }
}

/**
 * Records a product view. Renders nothing; sits on product pages so viewing
 * a product adds it to the front of the recently-viewed list.
 */
class RecentlyViewedTracker extends HTMLElement {
  connectedCallback() {
    const handle = this.dataset.handle;
    if (!handle) return;

    const handles = getStoredHandles().filter((stored) => stored !== handle);
    handles.unshift(handle);
    setStoredHandles(handles.slice(0, MAX_STORED_HANDLES));
  }
}

/**
 * Renders the recently viewed products carousel. Stays aria-hidden (and
 * therefore visually hidden, see aria-hidden:hidden in
 * sections/recently-viewed-products.liquid) unless it manages to render at
 * least one product card.
 */
class RecentlyViewedProducts extends HTMLElement {
  connectedCallback() {
    const wrapper = this.querySelector('.swiper-wrapper');
    const fetchSectionId = this.dataset.fetchSectionId;
    if (!wrapper || !fetchSectionId) return;

    const limit = parseInt(this.dataset.limit, 10) || 8;
    const excludeHandle = this.dataset.excludeHandle;
    const handles = getStoredHandles()
      .filter((handle) => handle !== excludeHandle)
      .slice(0, limit);

    if (handles.length === 0) return;

    this.#renderProducts(handles, wrapper, fetchSectionId);
  }

  /**
   * @param {string[]} handles
   * @param {Element} wrapper
   * @param {string} fetchSectionId
   */
  async #renderProducts(handles, wrapper, fetchSectionId) {
    const results = await Promise.all(
      handles.map((handle) => this.#fetchProductCard(handle, fetchSectionId))
    );

    // Only ever prune a handle on a confirmed 404 (product deleted/unpublished).
    // Any other failure (network hiccup, 5xx, etc.) is left alone so a
    // transient error can't wipe someone's viewing history.
    const staleHandles = handles.filter((handle, index) => results[index].stale);
    if (staleHandles.length > 0) {
      setStoredHandles(getStoredHandles().filter((handle) => !staleHandles.includes(handle)));
    }

    const fragment = document.createDocumentFragment();
    results.forEach(({ markup }) => {
      if (!markup) return;

      const slide = document.createElement('div');
      slide.className =
        'swiper-slide mr-4 w-[calc((100%-16px)/2)] shrink-0 last:mr-0 md:w-[calc((100%-64px)/5)]';
      slide.innerHTML = markup;
      fragment.appendChild(slide);
    });

    if (fragment.childElementCount === 0) return;

    wrapper.appendChild(fragment);
    this.setAttribute('aria-hidden', 'false');

    const swiperEl = wrapper.closest('.swiper');
    if (swiperEl?.swiper) swiperEl.swiper.update();
  }

  /**
   * @param {string} handle
   * @param {string} fetchSectionId
   * @returns {Promise<{markup: string|null, stale: boolean}>} The product
   *   card's outer HTML (or null if it couldn't be rendered), and whether the
   *   product is confirmed gone (HTTP 404) and safe to prune from storage.
   */
  async #fetchProductCard(handle, fetchSectionId) {
    try {
      const response = await fetch(`/products/${handle}?section_id=${fetchSectionId}`);
      if (response.status === 404) return { markup: null, stale: true };
      if (!response.ok) return { markup: null, stale: false };

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const card = doc.querySelector('.product-card');
      return { markup: card ? card.outerHTML : null, stale: false };
    } catch (error) {
      console.error('Error fetching recently viewed product:', error);
      return { markup: null, stale: false };
    }
  }
}

if (!customElements.get('recently-viewed-tracker')) {
  customElements.define('recently-viewed-tracker', RecentlyViewedTracker);
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
